'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { Globe2, Building2, TrendingUp } from 'lucide-react';

export function FTZAbout() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            variants={fadeIn('right')}
            className="space-y-6"
          >
            <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider">Understanding FTZ</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
              What Is a Foreign-Trade Zone?
            </h3>
            
            <p className="text-gray-700 text-lg leading-relaxed">
              A Foreign-Trade Zone is a designated area within the United States where goods can be imported, stored, handled, or manufactured without being subject to customs duties until they enter U.S. commerce. This setup allows companies to defer, reduce, or even eliminate certain tariffs, enhancing cash flow and operational flexibility.
            </p>

            <div className="grid gap-6 mt-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
                  <Globe2 size={24} />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Global Trade Hub</h4>
                  <p className="text-gray-700">Connect with international markets while maintaining U.S.-based operations.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
                  <Building2 size={24} />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Strategic Location</h4>
                  <p className="text-gray-700">Located in Milwaukee's FTZ #41, offering prime access to major transportation routes.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center text-brand-600 flex-shrink-0">
                  <TrendingUp size={24} />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Cost Optimization</h4>
                  <p className="text-gray-700">Reduce, defer, or eliminate duties while streamlining your supply chain.</p>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "show" : "hidden"}
            variants={fadeIn('left')}
            className="relative"
          >
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-brand-600 rounded-tl-2xl"></div>
            <img 
              src="https://images.pexels.com/photos/2226458/pexels-photo-2226458.jpeg"
              alt="Global Trade Operations" 
              className="rounded-lg shadow-xl relative z-10 w-full h-full object-cover"
            />
            <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-brand-100 rounded-br-2xl"></div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}